import { onUnmounted, ref, watch, type Ref } from 'vue'

/**
 * The pace the last count to move taught us, shared by every one on the page — module scope, so it
 * outlives any single caller. Only the first service of a freshly-loaded page has nothing to go on: after
 * that they are all checkpointing on the same engine at the same cadence, so what one measured is the
 * best guess the next one has before it has measured anything itself.
 */
let pageWidePace = 0

const SHORTEST = 300
const LONGEST = 8000

/**
 * What to spread a climb over when nothing has been measured anywhere yet. Near the cadence services
 * really report at, and deliberately at the long end of it: overshooting means the next count redirects a
 * climb still in motion, which is invisible, where undershooting means arriving early and standing still.
 */
const FIRST_CLIMB = 3000

/**
 * How much of the expected gap a climb is spread over. Above 1, so the climb is still running when the
 * next update lands and that update simply redirects it. Under 1 it would finish early and the number
 * would sit still until the next one — a ramp and a pause, which reads as the run stuttering when it is
 * working steadily.
 */
const AIM_LONG = 1.2

/** Most of the last gap, some of the one before, so a single slow checkpoint does not set the pace. */
const SMOOTHING = 0.6

const snaps = () =>
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * A number as the eye should see it: where it has climbed to, rather than the last figure to arrive.
 * Counts reach a screen in steps — one per cursor checkpoint — and a figure that jumps between them reads
 * as a run that stutters, where the same figure climbing reads as one working steadily.
 *
 * <p>Shared rather than kept inside the component that shows the number, because more than one thing is
 * drawn from it: the figure and the bar beside it have to move as one, and a bar with a CSS transition of
 * its own would lag the number it illustrates.</p>
 *
 * @param value the figure as it arrives, in steps
 * @param climbing whether it is still being added to — a finished service's number is what it came to,
 *   and a run opened from history would otherwise animate a figure that stopped moving days ago
 */
export function useClimbingNumber(value: () => number, climbing: () => boolean): Ref<number> {
  // Where the number is on screen, which lags where it actually is. Starts at whatever was already there:
  // a figure this was created holding is the state of things, not something the reader watched happen —
  // which is what separates opening a run from watching one.
  const shown = ref(value())
  let frame = 0

  // How far apart updates have been arriving, which is what a climb is spread over. Seeded from whatever
  // the page has already learned, and replaced by this caller's own measurements once it has two updates
  // to measure between. Never seeded from how long the first update took to arrive: that wait is the
  // service starting up — taking its place, reading its parent — and pacing by it would have the first
  // climb crawl for as long as the startup did.
  let paceMs = pageWidePace
  let lastUpdateAt = 0

  watch(value, (to) => {
    cancelAnimationFrame(frame)

    const now = performance.now()
    if (lastUpdateAt > 0) {
      const gap = now - lastUpdateAt
      paceMs = paceMs === 0 ? gap : paceMs * (1 - SMOOTHING) + gap * SMOOTHING
      pageWidePace = paceMs
    }
    lastUpdateAt = now

    // A number that went down is a correction — the exact count replacing the estimate the service was
    // adding up as it went. Corrections are not progress and are shown as they are.
    if (!climbing() || to < shown.value || snaps()) {
      shown.value = to
      return
    }

    climb(to, paceMs === 0 ? FIRST_CLIMB : Math.min(LONGEST, Math.max(SHORTEST, paceMs * AIM_LONG)))
  })

  // A service that has stopped counting shows what it came to, at once. Its last figure arrives while it
  // is still running and is paced like every step before it — long enough that a short service would
  // still be climbing to its final number while the services after it are already working.
  watch(climbing, (still) => {
    if (still) return
    cancelAnimationFrame(frame)
    shown.value = value()
  })

  // Linear, deliberately. An eased climb decelerates to a stop, which reads as the work finishing every
  // time a checkpoint lands; a constant rate reads as the work continuing. From where the eye is, not
  // from the value this one replaced: a climb cut short leaves the two apart, and starting at the value
  // would jump the number forward before climbing.
  function climb(to: number, over: number) {
    const from = shown.value
    const started = performance.now()
    const step = (at: number) => {
      const through = Math.min(1, (at - started) / over)
      shown.value = Math.round(from + (to - from) * through)
      if (through < 1) frame = requestAnimationFrame(step)
    }
    frame = requestAnimationFrame(step)
  }

  onUnmounted(() => cancelAnimationFrame(frame))

  return shown
}
